/**
 * Layanan Asisten AI — endpoint /api/asisten (AsistenController.cs).
 *
 * Kirim pesan memakai `fetch` (bukan axios): axios di browser tidak bisa membaca aliran
 * Server-Sent Events. Karena itu header token & tahun buku ditambahkan manual di sini.
 */
import { api } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import type {
  DetailPercakapanAsisten,
  PercakapanAsisten,
  PeristiwaAsisten,
  StatusAsisten,
  TiketAsisten,
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

function headerAliran(): HeadersInit {
  const header: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
  const token = localStorage.getItem('accessToken');
  if (token) header.Authorization = `Bearer ${token}`;
  const { TahunBukuAktif, JenisTahunBuku } = useAppStore.getState();
  if (TahunBukuAktif) header['X-Tahun-Buku'] = TahunBukuAktif.toString();
  if (JenisTahunBuku) header['X-Jenis-Tahun-Buku'] = JenisTahunBuku;
  return header;
}

/** Urai satu blok SSE ("event: x\ndata: {...}") menjadi peristiwa; null bila tidak lengkap. */
export function uraiBlokSse(blok: string): PeristiwaAsisten | null {
  const data = blok
    .split('\n')
    .filter((baris) => baris.startsWith('data:'))
    .map((baris) => baris.slice(5).trimStart())
    .join('\n');
  if (!data) return null;
  try {
    return JSON.parse(data) as PeristiwaAsisten;
  } catch {
    return null;
  }
}

async function postAliran(body: unknown, signal: AbortSignal): Promise<Response> {
  return fetch(`${API_BASE_URL}/asisten/pesan`, {
    method: 'POST',
    headers: headerAliran(),
    body: JSON.stringify(body),
    signal,
  });
}

export const asistenService = {
  /** Lewat axios — sekaligus memicu refresh token oleh interceptor bila perlu. */
  async getStatus(): Promise<StatusAsisten> {
    const { data } = await api.get<StatusAsisten>('/asisten/status');
    return data;
  },

  async getDaftarPercakapan(): Promise<PercakapanAsisten[]> {
    const { data } = await api.get<PercakapanAsisten[]>('/asisten/percakapan');
    return data;
  },

  async getPercakapan(idPercakapan: string): Promise<DetailPercakapanAsisten> {
    const { data } = await api.get<DetailPercakapanAsisten>(`/asisten/percakapan/${encodeURIComponent(idPercakapan)}`);
    return data;
  },

  /** `alasan` opsional, hanya untuk 👎 — bahan tinjauan perbaikan Asisten. */
  async beriRating(idPesan: number, rating: number, alasan?: string): Promise<void> {
    await api.post(`/asisten/pesan/${idPesan}/rating`, { rating, alasan });
  },

  async hapusRiwayat(): Promise<number> {
    const { data } = await api.delete<{ jumlahPercakapan: number }>('/asisten/riwayat');
    return data.jumlahPercakapan;
  },

  async buatTiket(ringkasan: string, menuTerkait?: string, idPercakapan?: string): Promise<TiketAsisten> {
    const { data } = await api.post<TiketAsisten>('/asisten/tiket', { ringkasan, menuTerkait, idPercakapan });
    return data;
  },

  /**
   * Kirim pesan dan terima jawaban mengalir. `onPeristiwa` dipanggil untuk setiap peristiwa.
   * Bila token kedaluwarsa (401), token disegarkan lewat `getStatus` lalu dicoba sekali lagi.
   */
  async kirimPesan(
    body: { pesan: string; idPercakapan?: string; menuAsal?: string },
    onPeristiwa: (peristiwa: PeristiwaAsisten) => void,
    signal: AbortSignal,
  ): Promise<void> {
    let respons = await postAliran(body, signal);
    if (respons.status === 401) {
      await asistenService.getStatus();
      respons = await postAliran(body, signal);
    }
    if (!respons.ok || !respons.body) {
      throw new Error(respons.status === 403
        ? 'Anda tidak memiliki akses ke Asisten.'
        : `Asisten tidak dapat dihubungi (HTTP ${respons.status}).`);
    }

    const pembaca = respons.body.getReader();
    const dekoder = new TextDecoder();
    let sisa = '';
    for (;;) {
      const { done, value } = await pembaca.read();
      if (done) break;
      sisa += dekoder.decode(value, { stream: true });
      let batas = sisa.indexOf('\n\n');
      while (batas >= 0) {
        const peristiwa = uraiBlokSse(sisa.slice(0, batas));
        sisa = sisa.slice(batas + 2);
        if (peristiwa) onPeristiwa(peristiwa);
        batas = sisa.indexOf('\n\n');
      }
    }
    const terakhir = uraiBlokSse(sisa);
    if (terakhir) onPeristiwa(terakhir);
  },
};
